import { Box, useMantineTheme } from '@mantine/core';
import { useState } from 'react';

import { resolveWhopIconUrl } from './resolveWhopIconUrl';

// Default size keeps every list row (icon or letter-avatar fallback) reporting the same height.
const DEFAULT_SIZE = 32;

type NetworkIconProps = {
  name: string;
  iconUrl: string | null;
  // EDGE-32115: callers that render the icon smaller (AssetIcon's corner badge) pass an explicit size so
  // the node is drawn at that size directly, instead of drawing 32px and shrinking with a CSS transform
  // (which, anchored top-left against a border-box border, pushed the placeholder off-centre).
  size?: number;
};

/** A network's icon, falling back to a letter avatar when there's no iconUrl or the <img> fails to load. */
export function NetworkIcon({ name, iconUrl, size = DEFAULT_SIZE }: NetworkIconProps) {
  const theme = useMantineTheme();
  const [errored, setErrored] = useState(false);
  const letter = name.charAt(0).toUpperCase();

  if (!iconUrl || errored) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          minWidth: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.colors.gray2[1],
          color: theme.colors.gray2[7],
          fontWeight: 600,
          // Scale the glyph only when rendered smaller than the default (the corner badge); the default
          // 32px avatar keeps its inherited font size, so existing callers (CryptoNetworkList) are unchanged.
          fontSize: size === DEFAULT_SIZE ? undefined : size * 0.5,
        }}
      >
        {letter}
      </Box>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={resolveWhopIconUrl(iconUrl)}
      alt=""
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: '50%',
        objectFit: 'cover',
      }}
      onError={() => setErrored(true)}
    />
  );
}
